import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Search, Trash2 } from 'lucide-react'
import { apiMethods } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import Button from '../components/Button'
import Input from '../components/Input'

const MIGRATION_COMPONENTS = ['database', 'media', 'plugins', 'themes']

function siteLabel(site) {
  if (!site) return '-'

  return site.name || site.canonical_host || site.host || site.home_url || site.site_url || site.site_uuid || '-'
}

function componentsLabel(components) {
  if (!Array.isArray(components) || components.length === 0) return '-'

  return components.join(', ')
}

function terminalMigrationStatus(status) {
  return ['completed', 'failed', 'cancelled'].includes(status)
}

function formatDateTime(value) {
  if (!value) return '-'

  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function siteStatus(site) {
  if (site.is_stale) {
    return 'Stale'
  }

  if (site.update_count > 0) {
    return `${site.update_count} updates`
  }

  return 'Current'
}

function siteStatusClass(site) {
  if (site.is_stale) return 'bg-red-100 text-red-700'
  if (site.update_count > 0) return 'bg-amber-100 text-amber-800'
  return 'bg-emerald-100 text-emerald-700'
}

export default function Maintenance() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isSuperAdmin } = useAuthStore()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [updatesOnly, setUpdatesOnly] = useState(false)
  const [tokenLabel, setTokenLabel] = useState('')
  const [tokenValue, setTokenValue] = useState('')
  const [projectName, setProjectName] = useState('')
  const [grantForm, setGrantForm] = useState({
    project_id: '',
    source_site_id: '',
    target_site_id: '',
    direction: 'pull',
    allowed_components: MIGRATION_COMPONENTS,
  })
  const [message, setMessage] = useState('')

  const queryParams = useMemo(
    () => ({
      page,
      per_page: 20,
      search: search || undefined,
      updates_available: updatesOnly || undefined,
    }),
    [page, search, updatesOnly],
  )

  const { data: sitesData, isLoading, isFetching } = useQuery({
    queryKey: ['maintenance-sites', queryParams],
    queryFn: () => apiMethods.getMaintenanceSites(queryParams),
  })

  const { data: tokensData } = useQuery({
    queryKey: ['maintenance-registration-tokens'],
    queryFn: () => apiMethods.getMaintenanceRegistrationTokens(),
  })

  const { data: siteOptionsData } = useQuery({
    queryKey: ['maintenance-site-options'],
    queryFn: () => apiMethods.getMaintenanceSites({ per_page: 100 }),
  })

  const { data: projectsData } = useQuery({
    queryKey: ['maintenance-projects'],
    queryFn: () => apiMethods.getMaintenanceProjects(),
  })

  const { data: grantsData } = useQuery({
    queryKey: ['maintenance-migration-grants'],
    queryFn: () => apiMethods.getMaintenanceMigrationGrants(),
  })

  const { data: migrationsData } = useQuery({
    queryKey: ['maintenance-migrations', { per_page: 10 }],
    queryFn: () => apiMethods.getMaintenanceMigrations({ per_page: 10 }),
  })

  const createTokenMutation = useMutation({
    mutationFn: () => apiMethods.createMaintenanceRegistrationToken({
      label: tokenLabel || undefined,
    }),
    onSuccess: (response) => {
      setTokenValue(response?.data?.token || '')
      setTokenLabel('')
      setMessage('Registration token created. It is shown once.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-registration-tokens'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to create token.')
    },
  })

  const deleteTokenMutation = useMutation({
    mutationFn: (tokenId) => apiMethods.deleteMaintenanceRegistrationToken(tokenId),
    onSuccess: () => {
      setMessage('Registration token revoked.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-registration-tokens'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to revoke token.')
    },
  })

  const requestReportMutation = useMutation({
    mutationFn: (siteId) => apiMethods.requestMaintenanceReport(siteId),
    onSuccess: () => {
      setMessage('Report request sent.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-sites'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Report request failed.')
    },
  })

  const deleteSiteMutation = useMutation({
    mutationFn: (siteId) => apiMethods.deleteMaintenanceSite(siteId),
    onSuccess: () => {
      setMessage('Maintenance site removed.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-sites'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to remove maintenance site.')
    },
  })

  const createProjectMutation = useMutation({
    mutationFn: () => apiMethods.createMaintenanceProject({
      name: projectName,
    }),
    onSuccess: () => {
      setProjectName('')
      setMessage('Migration project created.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-projects'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to create migration project.')
    },
  })

  const createGrantMutation = useMutation({
    mutationFn: (payload) => apiMethods.createMaintenanceMigrationGrant(payload),
    onSuccess: () => {
      setMessage('Migration grant created.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-migration-grants'] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-projects'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to create migration grant.')
    },
  })

  const deleteGrantMutation = useMutation({
    mutationFn: (grantId) => apiMethods.deleteMaintenanceMigrationGrant(grantId),
    onSuccess: () => {
      setMessage('Migration grant removed.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-migration-grants'] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-projects'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to remove migration grant.')
    },
  })

  const cancelMigrationMutation = useMutation({
    mutationFn: (migrationId) => apiMethods.cancelMaintenanceMigration(migrationId),
    onSuccess: () => {
      setMessage('Migration job cancelled.')
      queryClient.invalidateQueries({ queryKey: ['maintenance-migrations'] })
    },
    onError: (error) => {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to cancel migration job.')
    },
  })

  const copyToken = async () => {
    if (!tokenValue) return
    try {
      await navigator.clipboard.writeText(tokenValue)
      setMessage('Token copied.')
    } catch {
      setMessage('Could not copy token. Select it manually.')
    }
  }

  const updateGrantComponent = (component, checked) => {
    setGrantForm((current) => ({
      ...current,
      allowed_components: checked
        ? [...new Set([...current.allowed_components, component])]
        : current.allowed_components.filter((value) => value !== component),
    }))
  }

  const createGrant = () => {
    if (!grantForm.project_id || !grantForm.source_site_id || !grantForm.target_site_id) {
      setMessage('Choose project, source site, and target site before creating grant.')
      return
    }

    if (grantForm.source_site_id === grantForm.target_site_id) {
      setMessage('Source and target site must be different.')
      return
    }

    if (grantForm.allowed_components.length === 0) {
      setMessage('Choose at least one migration component.')
      return
    }

    createGrantMutation.mutate({
      project_id: Number(grantForm.project_id),
      source_site_id: Number(grantForm.source_site_id),
      target_site_id: Number(grantForm.target_site_id),
      direction: grantForm.direction,
      allowed_components: grantForm.allowed_components,
      enabled: true,
    })
  }

  if (!isSuperAdmin()) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        You do not have permission to access Maintenance.
      </div>
    )
  }

  const sites = sitesData?.data || []
  const meta = sitesData?.meta
  const tokens = tokensData?.data || []
  const projects = projectsData?.data || []
  const grants = grantsData?.data || []
  const siteOptions = siteOptionsData?.data || sites
  const migrationJobs = migrationsData?.data || []
  const unusedTokens = tokens.filter((token) => !token.is_used)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Maintenance</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track WordPress core and plugin versions across maintained sites.
        </p>
      </div>

      {message ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
          {message}
        </div>
      ) : null}

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Create Registration Token</h2>
            <p className="mt-1 text-sm text-gray-500">
              Token appears once. Paste it into Maintenance Reporter on WordPress site.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input
                value={tokenLabel}
                onChange={(event) => setTokenLabel(event.target.value)}
                placeholder="Token label, e.g. client domain"
              />
              <Button
                type="button"
                onClick={() => createTokenMutation.mutate()}
                disabled={createTokenMutation.isPending}
              >
                {createTokenMutation.isPending ? 'Creating...' : 'Create Token'}
              </Button>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {unusedTokens.length} unused token{unusedTokens.length === 1 ? '' : 's'}
          </div>
        </div>

        {tokenValue ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-amber-800">
              Copy now
            </div>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <code className="block flex-1 overflow-x-auto rounded bg-white px-3 py-2 text-xs text-gray-800">
                {tokenValue}
              </code>
              <Button type="button" variant="secondary" onClick={copyToken}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {unusedTokens.length > 0 ? (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-semibold text-gray-900">Unused Tokens</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Token
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Expires
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {unusedTokens.map((token) => (
                  <tr key={token.id}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{token.label || '-'}</div>
                      <div className="text-xs text-gray-500">{token.token_prefix || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDateTime(token.expires_at)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        onClick={() => deleteTokenMutation.mutate(token.id)}
                        disabled={deleteTokenMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Migration Control Plane</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage projects and directional grants used by Maintenance Reporter migrations.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder="Project name"
            />
            <Button
              type="button"
              onClick={() => {
                if (!projectName.trim()) {
                  setMessage('Project name is required.')
                  return
                }
                createProjectMutation.mutate()
              }}
              disabled={createProjectMutation.isPending}
            >
              {createProjectMutation.isPending ? 'Creating...' : 'Create Project'}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="rounded-md border border-gray-200 p-3">
            <div className="text-sm font-semibold text-gray-900">Projects</div>
            <div className="mt-3 space-y-2">
              {projects.length === 0 ? (
                <div className="text-sm text-gray-500">No migration projects yet.</div>
              ) : (
                projects.map((project) => (
                  <div key={project.id} className="rounded border border-gray-100 px-3 py-2 text-sm">
                    <div className="font-medium text-gray-900">{project.name}</div>
                    <div className="text-xs text-gray-500">
                      {project.slug} - {project.sites_count} sites - {project.migration_grants_count} grants
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-md border border-gray-200 p-3 lg:col-span-2">
            <div className="text-sm font-semibold text-gray-900">Create Grant</div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="text-sm text-gray-700">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Project</span>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={grantForm.project_id}
                  onChange={(event) => setGrantForm((current) => ({ ...current, project_id: event.target.value }))}
                >
                  <option value="">Choose project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-700">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Direction</span>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={grantForm.direction}
                  onChange={(event) => setGrantForm((current) => ({ ...current, direction: event.target.value }))}
                >
                  <option value="pull">Pull</option>
                  <option value="push">Push</option>
                  <option value="both">Both</option>
                </select>
              </label>
              <label className="text-sm text-gray-700">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Source</span>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={grantForm.source_site_id}
                  onChange={(event) => setGrantForm((current) => ({ ...current, source_site_id: event.target.value }))}
                >
                  <option value="">Choose source</option>
                  {siteOptions.map((site) => (
                    <option key={site.id} value={site.id}>{siteLabel(site)}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-700">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Target</span>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={grantForm.target_site_id}
                  onChange={(event) => setGrantForm((current) => ({ ...current, target_site_id: event.target.value }))}
                >
                  <option value="">Choose target</option>
                  {siteOptions.map((site) => (
                    <option key={site.id} value={site.id}>{siteLabel(site)}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {MIGRATION_COMPONENTS.map((component) => (
                <label key={component} className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={grantForm.allowed_components.includes(component)}
                    onChange={(event) => updateGrantComponent(component, event.target.checked)}
                  />
                  {component}
                </label>
              ))}
            </div>
            <div className="mt-3">
              <Button type="button" onClick={createGrant} disabled={createGrantMutation.isPending}>
                {createGrantMutation.isPending ? 'Creating...' : 'Create Grant'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Grant</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Access</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Components</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {grants.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={4}>No migration grants configured.</td>
                </tr>
              ) : (
                grants.map((grant) => (
                  <tr key={grant.id}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{grant.project?.name || 'Unassigned project'}</div>
                      <div className="text-xs text-gray-500">{grant.direction} - {grant.is_active ? 'active' : 'inactive'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{siteLabel(grant.source_site)} -&gt; {siteLabel(grant.target_site)}</div>
                      <div className="text-xs text-gray-500">expires: {formatDateTime(grant.expires_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{componentsLabel(grant.allowed_components)}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        disabled={deleteGrantMutation.isPending}
                        onClick={() => {
                          if (window.confirm('Remove this migration grant?')) {
                            deleteGrantMutation.mutate(grant.id)
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Recent Migration</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Sites</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Components</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {migrationJobs.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={4}>No migration jobs yet.</td>
                </tr>
              ) : (
                migrationJobs.map((job) => (
                  <tr key={job.id}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{job.status} - {job.current_phase || 'no phase'}</div>
                      <div className="text-xs text-gray-500">{job.direction} - {formatDateTime(job.created_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{siteLabel(job.source_site)} -&gt; {siteLabel(job.target_site)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{componentsLabel(job.components)}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={terminalMigrationStatus(job.status) || cancelMigrationMutation.isPending}
                        onClick={() => cancelMigrationMutation.mutate(job.id)}
                      >
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-10"
              value={search}
              onChange={(event) => {
                setPage(1)
                setSearch(event.target.value)
              }}
              placeholder="Search site name, URL, UUID"
            />
          </div>
          <label className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={updatesOnly}
              onChange={(event) => {
                setPage(1)
                setUpdatesOnly(event.target.checked)
              }}
            />
            Updates only
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Site
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  WordPress
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Plugins
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Last Report
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={5}>
                    Loading sites...
                  </td>
                </tr>
              ) : sites.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={5}>
                    No maintenance sites found.
                  </td>
                </tr>
              ) : (
                sites.map((site) => (
                  <tr
                    key={site.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/maintenance/${site.id}`)}
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{site.name || site.home_url || '-'}</div>
                      <div className="text-gray-500">{site.home_url || site.site_url || '-'}</div>
                      <div className="text-xs text-gray-400">{site.site_uuid}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{site.wordpress_version || '-'}</div>
                      <div className="text-xs text-gray-500">
                        latest: {site.wordpress_latest_version || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{site.plugins_count} installed</div>
                      <div className="text-xs text-gray-500">{site.active_plugins_count} active</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-600">{formatDateTime(site.last_reported_at)}</div>
                      <span className={`mt-1 inline-flex rounded-full px-2 py-1 text-xs font-medium ${siteStatusClass(site)}`}>
                        {siteStatus(site)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={requestReportMutation.isPending || deleteSiteMutation.isPending}
                          onClick={(event) => {
                            event.stopPropagation()
                            requestReportMutation.mutate(site.id)
                          }}
                        >
                          Request Report
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          aria-label="Remove site"
                          title="Remove site"
                          disabled={deleteSiteMutation.isPending}
                          onClick={(event) => {
                            event.stopPropagation()
                            if (window.confirm('Remove this maintained site and its report history?')) {
                              deleteSiteMutation.mutate(site.id)
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-500">
            {meta ? `Page ${meta.current_page} of ${meta.last_page} (${meta.total} sites)` : 'No pagination data'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={!meta || page <= 1 || isFetching}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => setPage((current) => current + 1)}
              disabled={!meta || page >= meta.last_page || isFetching}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
